// Shopping together (045_shared_trips.sql): a household has at most one open
// trip, and everyone shopping is in it. Start shopping while someone else is
// shopping joins their trip; Finish ends it for everyone. The summary and the
// cost are the finisher's, on the device they finished on (046); everyone
// still shopping gets a note with Keep shopping, and a watch finish asks its
// cost on the phone once. The database decides (start_trip, finish_trip,
// leave_trip); this is what the phone reads off the rows it has.
import { STALE_BASKET_MS } from './constants'
import { clock } from './trips'
import type { Member, Trip, TripShopper } from './supabase'

/** Someone else finishing the trip is news for this long: after it, Keep shopping is no help. */
export const NOTICE_MS = 3 * 60 * 60 * 1000

/** What Start shopping does for this person: start one, carry on with theirs, or join. */
export type TripRole = 'none' | 'mine' | 'join'

/** The household's open trip: unfinished and started within STALE_BASKET_MS. */
export function openTripOf(rows: readonly Trip[], now: number): Trip | null {
  return rows.find(t => !t.finished_at && now - Date.parse(t.started_at) <= STALE_BASKET_MS) ?? null
}

/** Who is shopping in a trip now, the first to join first. */
function activeIn(trip: Trip, shoppers: readonly TripShopper[]): TripShopper[] {
  return shoppers
    .filter(s => s.trip_id === trip.id && !s.left_at)
    .sort((a, b) => a.joined_at.localeCompare(b.joined_at))
}

export function roleOf(trip: Trip | null, shoppers: readonly TripShopper[], memberId: string | null): TripRole {
  if (!trip) return 'none'
  const inTrip = shoppers.filter(s => s.trip_id === trip.id)
  // Before the shoppers are read, the starter's own trip is theirs.
  if (!inTrip.length) return trip.started_by === memberId ? 'mine' : 'join'
  return activeIn(trip, shoppers).some(s => s.member_id === memberId) ? 'mine' : 'join'
}

/** The member to name on "Join …": the first of the others shopping, else whoever started it. */
export function joinWith(trip: Trip | null, shoppers: readonly TripShopper[], memberId: string | null): string | null {
  if (!trip) return null
  const other = activeIn(trip, shoppers).find(s => s.member_id !== memberId)
  if (other) return other.member_id
  return trip.started_by && trip.started_by !== memberId ? trip.started_by : null
}

/** A member's name for "Join …", or null when unknown. */
export function memberName(members: readonly Pick<Member, 'id' | 'display_name'>[], id: string | null): string | null {
  return id ? members.find(m => m.id === id)?.display_name ?? null : null
}

/** The note when someone else finished: "Anna finished the trip · 23 items · 24:10". */
export function noticeText(trip: Pick<Trip, 'items' | 'started_at' | 'finished_at'>, name: string | null): string {
  const parts = [`${name ?? 'Someone'} finished the trip`]
  if (trip.items) parts.push(trip.items === 1 ? '1 item' : `${trip.items} items`)
  const ms = trip.finished_at ? Date.parse(trip.finished_at) - Date.parse(trip.started_at) : 0
  if (ms >= 60_000) parts.push(clock(ms))
  return parts.join(' · ')
}

export function shopLabel(role: TripRole, name: string | null): string {
  if (role === 'mine') return 'Continue shopping'
  if (role === 'join') return name ? `Join ${name}` : 'Join the trip'
  return 'Start shopping'
}

/** Whether this person was still shopping in a trip when it ended (not left), or started it before shoppers were kept. */
function shoppedTo(t: Trip, shoppers: readonly TripShopper[], memberId: string): boolean {
  const me = shoppers.find(s => s.trip_id === t.id && s.member_id === memberId)
  return me ? !me.left_at : t.started_by === memberId
}

/**
 * The latest trip someone else finished while this person was still shopping
 * in it, not yet shown here: a note ("Anna finished · Keep shopping"), never
 * the summary or the cost — those are the finisher's. Not one they left
 * before it ended, nor an old trip closed as abandoned (no finisher).
 */
export function tripNoticeFor(
  rows: readonly Trip[], shoppers: readonly TripShopper[], memberId: string | null,
  seen: readonly string[], now: number,
): Trip | null {
  if (!memberId) return null
  return rows.find(t => t.finished_at && t.finished_by && t.finished_by !== memberId && !t.deleted_at
    && !seen.includes(t.id) && now - Date.parse(t.finished_at) <= NOTICE_MS
    && shoppedTo(t, shoppers, memberId)) ?? null
}


/** "just now", "4 min ago", "2 h ago". */
function sinceText(at: number, now: number): string {
  const minutes = Math.floor((now - at) / 60_000)
  if (minutes < 1) return 'just now'
  return minutes < 60 ? `${minutes} min ago` : `${Math.floor(minutes / 60)} h ago`
}

/**
 * End trip's question, for someone who isn't shopping in it: whose trip, and
 * how live it looks (a forgotten one hasn't seen a tick for a while), so a
 * trip still going isn't ended by accident.
 */
export function endTripText(
  trip: Pick<Trip, 'started_at'>, basket: readonly { done_at: string | null }[], name: string | null, now: number,
): { title: string; facts: string } {
  const parts = [`Started ${sinceText(Date.parse(trip.started_at), now)}`]
  const ticks = basket.flatMap(i => (i.done_at ? [Date.parse(i.done_at)] : []))
  if (ticks.length) parts.push(`last tick ${sinceText(Math.max(...ticks), now)}`)
  parts.push(basket.length ? `${basket.length} in the basket` : 'nothing in the basket')
  return { title: name ? `End ${name}'s trip?` : 'End the trip?', facts: parts.join(' · ') }
}
